import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ContentCopyOutlined } from "@mui/icons-material";
import { buildCookbookPrompt, PROMPT_LANGUAGES } from "../../common/cookbook";

// Specialty and language are remembered in this browser between visits.
const readPref = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const savePref = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / blocked storage: nothing to remember
  }
};

// Builds the prompt that asks an AI chat (Claude, ChatGPT, ...) for a cookbook note on a topic.
const PromptDialog = ({ open, canEdit, onClose, onAddTopic }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const promptField = useRef(null);
  const [topic, setTopic] = useState("");
  const [specialty, setSpecialty] = useState(() => readPref("cookbookSpecialty", ""));
  const [language, setLanguage] = useState(() => readPref("cookbookLanguage", "en"));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setCopied(false);
    }
  }, [open]);

  const prompt = buildCookbookPrompt({ topic, specialty, language });

  const handleCopy = async () => {
    savePref("cookbookSpecialty", specialty.trim());
    savePref("cookbookLanguage", language);
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      promptField.current.select();
      document.execCommand("copy");
    }
    setCopied(true);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Ask AI for a topic</DialogTitle>
      <DialogContent>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "2fr 2fr 1fr" }}
          gap="1rem"
          sx={{ mt: "0.5rem" }}
        >
          <TextField
            autoFocus
            label="Topic"
            placeholder="e.g. Diverticulitis"
            value={topic}
            onChange={(e) => {
              setTopic(e.target.value);
              setCopied(false);
            }}
          />
          <TextField
            label="Your specialty (optional)"
            placeholder="e.g. general surgery"
            value={specialty}
            onChange={(e) => {
              setSpecialty(e.target.value);
              setCopied(false);
            }}
          />
          <TextField
            select
            label="Language"
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value);
              setCopied(false);
            }}
          >
            {Object.entries(PROMPT_LANGUAGES).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={8}
          maxRows={14}
          label="Prompt"
          value={prompt}
          inputRef={promptField}
          InputProps={{
            readOnly: true,
            sx: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: "0.8rem" },
          }}
          sx={{ mt: "1.25rem" }}
        />

        {copied && (
          <Alert severity="success" sx={{ mt: "1rem" }}>
            Prompt copied. Paste it into Claude, ChatGPT or any other AI chat
            {canEdit ? ", then bring the Markdown it writes back here with Add topic." : "."}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canEdit && (
          <Button color="secondary" onClick={onAddTopic}>
            Add topic
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ContentCopyOutlined />}
          onClick={handleCopy}
          disabled={!topic.trim()}
        >
          Copy prompt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptDialog;
